import type { CSSProperties } from 'react';
import { getStrings, localePath, type Lang } from '../lib/i18n';
import { StoreBadges } from './StoreBadges';
import { STORE_IOS, STORE_ANDROID } from '../lib/constants';
import { IPhoneFrame } from './IPhoneFrame';
import { localizedScreenshot } from '../lib/screenshots';

type Vars = CSSProperties & Record<`--${string}`, string | number>;

// Small decorative stickers that bob around the phone (desktop positions).
const stickers = [
  { emoji: '💪', pos: 'left-[-2.5rem] top-[8%]', r: '-10deg', edge: 'var(--pop-5)' },
  { emoji: '🔥', pos: 'right-[-1.5rem] top-[2%]', r: '8deg', edge: 'var(--pop-3)' },
  { emoji: '🏆', pos: 'right-[-2.75rem] top-[46%]', r: '-6deg', edge: 'var(--pop-4)' },
  { emoji: '⏱️', pos: 'left-[-1rem] bottom-[6%]', r: '7deg', edge: 'var(--pop-2)' },
];

/**
 * Signature piece: a chunky "strength" card whose left/right arm bars fill on a
 * loop and pop a PR badge when they top out. Decorative (aria-hidden); the
 * label reuses an existing localized string, everything else is numbers.
 */
function StrengthCard({ label }: { label: string }) {
  const rows = [
    { arm: 'L', from: '70', to: '72.5', d: '0ms', w: '0.82' },
    { arm: 'R', from: '77.5', to: '80', d: '300ms', w: '0.94' },
  ];
  return (
    <div
      className="card-chunky relative w-[236px] p-4 text-left"
      style={{ '--edge': 'var(--brand)' } as Vars}
      aria-hidden="true"
    >
      <div className="flex items-center gap-2.5 mb-3.5">
        <span className="chip-icon w-9 h-9 text-lg" style={{ '--chip': 'var(--brand)' } as Vars}>
          💪
        </span>
        <span className="font-heading font-black text-ink text-[0.95rem] leading-tight line-clamp-2">{label}</span>
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.arm} className="flex items-center gap-2.5">
            <span className="w-6 h-6 rounded-lg bg-ink text-dark text-xs font-black flex items-center justify-center">
              {r.arm}
            </span>
            <div className="relative flex-1 h-3.5 rounded-full bg-elevated overflow-hidden">
              <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Number(r.w) * 100}%` }}>
                <div
                  className="fill-loop h-full w-full rounded-full bg-gradient-to-r from-[var(--brand-2)] to-[var(--brand)]"
                  style={{ '--d': r.d } as Vars}
                />
              </div>
            </div>
            <span className="w-14 text-right font-heading font-black text-ink text-sm tabular-nums">
              {r.to}
              <span className="text-faint text-[10px] font-bold ml-0.5">kg</span>
            </span>
          </div>
        ))}
      </div>
      {/* PR badge pops when the bars top out */}
      <div className="absolute -top-4 -right-4">
        <div className="pr-pop">
          <span
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-black text-[#3a2a00]"
            style={{ background: 'var(--pop-4)', boxShadow: '0 4px 0 var(--pop-4-edge)' }}
          >
            🏆 +2.5
          </span>
        </div>
      </div>
    </div>
  );
}

export const Hero = ({ lang = 'en' as Lang }: { lang?: Lang }) => {
  const t = getStrings(lang);
  const s = t.store;

  return (
    <section className="relative overflow-hidden -mt-20 pt-28 pb-20 md:pt-32 lg:pt-36 lg:pb-28">
      {/* Background: static radial blobs + dotted grid (no blur filters) */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="dot-grid absolute inset-0" />
        <div className="blob -top-40 -left-40 h-[34rem] w-[34rem]" />
        <div className="blob top-1/3 -right-40 h-[30rem] w-[30rem]" style={{ '--blob': 'var(--brand-2)' } as Vars} />
      </div>

      <div className="container mx-auto px-5 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-8 items-center">
          {/* Left: copy */}
          <div className="text-center lg:text-left">
            <div className="pop-in mb-7" style={{ '--d': '80ms' } as Vars}>
              <a
                href={localePath(lang, '/east-vs-west')}
                className="eyebrow-pill normal-case tracking-normal text-sm hover:brightness-95 transition"
              >
                New — Follow East vs West 28 →
              </a>
            </div>

            <h1 className="font-heading text-[2.6rem] sm:text-6xl xl:text-7xl font-black tracking-tight text-ink leading-[1.03]">
              {t.hero.title}
            </h1>
            <svg
              aria-hidden="true"
              viewBox="0 0 300 20"
              preserveAspectRatio="none"
              className="mt-3 h-4 w-48 sm:w-64 mx-auto lg:mx-0 overflow-visible"
              fill="none"
            >
              <path
                className="draw"
                style={{ '--d': '500ms' } as Vars}
                d="M3 14 C 50 3, 90 3, 130 11 S 220 19, 297 6"
                stroke="var(--brand-2)"
                strokeWidth="7"
                strokeLinecap="round"
                pathLength={1}
              />
            </svg>

            <p className="mt-6 text-lg md:text-xl text-muted mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="pop-in flex flex-row flex-wrap items-center justify-center lg:justify-start gap-3" style={{ '--d': '300ms' } as Vars}>
              <StoreBadges iosHref={STORE_IOS} androidHref={STORE_ANDROID} s={s} lang={lang} />
            </div>
          </div>

          {/* Right: static tilted phones + strength card + bobbing stickers */}
          <div className="flex justify-center">
            <div className="relative w-[230px] sm:w-[250px] lg:w-[400px] lg:h-[560px]">
              {/* Secondary phone — desktop only, static tilt behind */}
              <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 rotate-6 z-0">
                <div className="phone-lift">
                  <IPhoneFrame
                    className="w-[210px] opacity-95"
                    src={localizedScreenshot('/images/app-hero-2.webp', lang)}
                    alt="ArmProgress progress tab — per-exercise strength chart"
                  />
                </div>
              </div>

              {/* Primary phone — static tilt; hover lift on the inner element */}
              <div className="relative z-10 mx-auto lg:mx-0 lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 -rotate-3 w-[230px] sm:w-[250px]">
                <div className="phone-lift">
                  <IPhoneFrame
                    className="w-full"
                    src={localizedScreenshot('/images/app-hero-1.webp', lang)}
                    alt="ArmProgress home dashboard — weekly streak, workout stats and events"
                    loading="eager"
                  />
                </div>
              </div>

              {/* Signature strength card */}
              <div className="absolute z-20 -bottom-12 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:-right-28 sm:bottom-6 lg:right-auto lg:left-[9.5rem] lg:bottom-[2%]">
                {/* pop-in animates transform, so it lives on an inner element */}
                <div className="pop-in" style={{ '--d': '600ms' } as Vars}>
                  <StrengthCard label={t.product.steps[1]} />
                </div>
              </div>

              {/* Bobbing stickers */}
              {stickers.map((st, i) => (
                <div
                  key={st.emoji}
                  className={`absolute z-20 ${st.pos} ${i > 1 ? 'hidden sm:block' : ''}`}
                  aria-hidden="true"
                >
                  <div className="pop-in" style={{ '--i': i, '--d': '700ms' } as Vars}>
                    <div className="bob" style={{ '--i': i, '--r': st.r } as Vars}>
                      <span
                        className="card-chunky flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center text-2xl sm:text-[1.7rem]"
                        style={{ '--edge': st.edge, borderRadius: '1rem' } as Vars}
                      >
                        {st.emoji}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
